import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { t } from 'i18next';
import { useSnackbar } from 'notistack';
import CloseIcon from '@mui/icons-material/Close';
import CarForm from './CarForm';
import InsuranceForm from '../financeComponents/InsuranceForm';

export type FleetMode = 'vehicles' | 'insurance';

interface FleetPanelProps {
  mode: FleetMode;
  access: any;
  baseUrl: string;
  onClose: () => void;
}

type BadgeKind = 'ok' | 'soon' | 'expired' | 'none';

const badge = (date: string | null): { kind: BadgeKind; label: string } => {
  if (!date) return { kind: 'none', label: '—' };
  const exp = new Date(date);
  const days = Math.ceil((exp.getTime() - Date.now()) / 86400000);
  if (days < 0) return { kind: 'expired', label: t('Expired') || 'Expired' };
  if (days <= 30) return { kind: 'soon', label: `${days} ${t('DaysShort') || 'd'}` };
  return { kind: 'ok', label: exp.toLocaleDateString('en-GB') };
};

const bStyle = (kind: BadgeKind): React.CSSProperties => {
  const map: Record<BadgeKind, [string, string]> = {
    ok: ['var(--color-success-bg)', 'var(--color-success)'],
    soon: ['var(--color-warning-bg)', 'var(--color-warning)'],
    expired: ['var(--color-danger-bg)', 'var(--color-danger)'],
    none: ['var(--btn-user-in)', 'var(--text-color-calendar-week)'],
  };
  const [bg, color] = map[kind];
  return { background: bg, color, fontSize: 9.5, fontWeight: 700, padding: '2px 7px', borderRadius: 5, whiteSpace: 'nowrap' };
};
const infoBadge: React.CSSProperties = { background: 'var(--color-info-bg)', color: 'var(--color-info)', fontSize: 9.5, fontWeight: 700, padding: '2px 7px', borderRadius: 5 };

const FleetPanel: React.FC<FleetPanelProps> = ({ mode, access, baseUrl, onClose }) => {
  const [view, setView] = useState<'list' | 'form'>('list');
  const [editId, setEditId] = useState<number | null>(null);
  const [cars, setCars] = useState<any[]>([]);
  const [policies, setPolicies] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const { enqueueSnackbar } = useSnackbar();

  const canAdd = access?.finance_fleet_add === true || access?.finance_fleet_add === 1;
  const canDelete = access?.finance_fleet_delete === true || access?.finance_fleet_delete === 1;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, p, u] = await Promise.allSettled([
        axios.get('cars'),
        axios.get('finance-insurance'),
        axios.get('users'),
      ]);
      if (c.status === 'fulfilled') setCars(Array.isArray(c.value.data) ? c.value.data : []);
      if (p.status === 'fulfilled') setPolicies(Array.isArray(p.value.data) ? p.value.data : []);
      if (u.status === 'fulfilled') setUsers(Array.isArray(u.value.data) ? u.value.data : []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // switching mode resets to list
  useEffect(() => { setView('list'); setEditId(null); }, [mode]);

  const carInsurance = useMemo(() => {
    const map = new Map<number, string | null>();
    for (const c of cars) {
      const dates = policies
        .filter((p) => (p.car_ids || []).includes(c.id))
        .map((p) => p.expiry_date)
        .filter(Boolean) as string[];
      map.set(c.id, dates.length ? dates.reduce((a, b) => (new Date(a) < new Date(b) ? a : b)) : null);
    }
    return map;
  }, [cars, policies]);

  const carsById = useMemo(() => new Map(cars.map((c) => [c.id, `${c.brand} ${c.model}`])), [cars]);

  const openForm = (id: number | null) => { setEditId(id); setView('form'); };
  const afterSave = () => { load(); setView('list'); setEditId(null); };

  const del = async (id: number, kind: FleetMode, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm(t('AreYouSure') || 'Are you sure?')) return;
    try {
      await axios.delete(kind === 'vehicles' ? `cars/${id}` : `finance-insurance/${id}`);
      enqueueSnackbar(t('Deleted') || 'Deleted', { variant: 'success' });
      load();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || 'Error', { variant: 'error' });
    }
  };

  const title = mode === 'vehicles' ? (t('CompanyVehicles') || 'Vehicles') : (t('Insurance') || 'Insurance');
  const icon = mode === 'vehicles' ? '🚗' : '🛡';

  // form view — reuse full forms (they render own header with close)
  if (view === 'form') {
    return mode === 'vehicles' ? (
      <CarForm carId={editId} baseUrl={baseUrl} onClose={() => setView('list')} onSaved={afterSave} />
    ) : (
      <InsuranceForm insuranceId={editId} baseUrl={baseUrl} onClose={() => setView('list')} onSaved={afterSave} />
    );
  }

  const items = mode === 'vehicles' ? cars : policies;

  return (
    <div className="w-full h-full flex flex-col bg-[var(--bg-form)] text-[var(--text-color)]" style={{ minHeight: 0 }}>
      <div className="flex justify-between items-center py-[10px] px-[13px] bg-[var(--bg-top)]" style={{ borderBottom: 'var(--border-input)' }}>
        <span className="text-[14px] font-bold flex items-center gap-[8px]">{icon} {title}</span>
        <div className="flex gap-[6px] items-center">
          {canAdd && (
            <button
              type="button"
              className="btn-primary"
              style={{ width: 30, height: 30, padding: 0, justifyContent: 'center' }}
              title={t('Add') as string}
              onClick={() => openForm(null)}
            >＋</button>
          )}
          <button type="button" className="btn-icon" title={t('Cancel') as string} onClick={onClose}>
            <CloseIcon sx={{ fontSize: 16 }} />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-[10px]" style={{ minHeight: 0 }}>
        {loading ? (
          <div className="text-center text-[12px] text-[var(--text-color-calendar-week)] py-[20px]">…</div>
        ) : items.length === 0 ? (
          <div className="text-center text-[12px] text-[var(--text-color-calendar-week)] py-[24px]">
            {mode === 'vehicles' ? (t('NoVehicles') || 'No vehicles') : (t('NoPolicies') || 'No policies')}
          </div>
        ) : mode === 'vehicles' ? (
          cars.map((c) => {
            const mot = badge(c.mot_expiry_date);
            const ins = badge(carInsurance.get(c.id) ?? null);
            const drivers = c.all_drivers
              ? (t('AnyEmployee') || 'Any')
              : `${(c.driver_ids || []).length} ${t('Drivers') || 'drivers'}`;
            return (
              <div
                key={c.id}
                onClick={() => openForm(c.id)}
                className="mb-[8px] cursor-pointer"
                style={{ background: 'var(--btn-user-form)', border: 'var(--border-input)', borderRadius: 8, padding: '10px 11px' }}
              >
                <div className="flex justify-between items-start gap-[8px]">
                  <div>
                    <div className="text-[13.5px] font-bold">{c.brand} {c.model}</div>
                    <div className="text-[11px] text-[var(--text-color-calendar-week)] mt-[1px]">
                      {[c.year, c.fuel_type, c.mileage != null ? `${c.mileage.toLocaleString()} km` : null].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                  <span style={{ fontFamily: 'ui-monospace,monospace', fontSize: 10.5, fontWeight: 800, letterSpacing: .5, padding: '2px 6px', border: '1.5px solid var(--text-color-calendar-week)', borderRadius: 5, whiteSpace: 'nowrap' }}>{c.plate_number}</span>
                </div>
                <div className="flex gap-[6px] mt-[9px] flex-wrap items-center">
                  <span style={bStyle(mot.kind)}>MOT {mot.label}</span>
                  <span style={bStyle(ins.kind)}>{t('Insurance') || 'Ins'} {ins.label}</span>
                  <span style={infoBadge}>{drivers}</span>
                  {canDelete && (
                    <button type="button" className="btn-icon danger" style={{ marginLeft: 'auto', width: 24, height: 24 }} onClick={(e) => del(c.id, 'vehicles', e)} title={t('Delete') as string}>🗑</button>
                  )}
                </div>
              </div>
            );
          })
        ) : (
          policies.map((p) => {
            const exp = badge(p.expiry_date);
            const carNames = (p.car_ids || []).map((id: number) => carsById.get(id)).filter(Boolean);
            return (
              <div
                key={p.id}
                onClick={() => openForm(p.id)}
                className="mb-[8px] cursor-pointer"
                style={{ background: 'var(--btn-user-form)', border: 'var(--border-input)', borderRadius: 8, padding: '10px 11px' }}
              >
                <div className="flex justify-between items-start gap-[8px]">
                  <div>
                    <div className="text-[13.5px] font-bold">{p.provider}</div>
                    <div className="text-[11px] text-[var(--text-color-calendar-week)] mt-[1px]">{p.policy_number}</div>
                  </div>
                  <span style={bStyle(exp.kind)}>{exp.label}</span>
                </div>
                <div className="flex gap-[6px] mt-[9px] flex-wrap items-center">
                  <span style={infoBadge}>{p.any_driver ? (t('AnyDriver') || 'Any driver') : `${(p.driver_ids || []).length} ${t('Drivers') || 'drivers'}`}</span>
                  <span style={{ ...bStyle('none') }}>{carNames.length ? carNames.join(', ') : `0 ${t('Cars') || 'cars'}`}</span>
                  {canDelete && (
                    <button type="button" className="btn-icon danger" style={{ marginLeft: 'auto', width: 24, height: 24 }} onClick={(e) => del(p.id, 'insurance', e)} title={t('Delete') as string}>🗑</button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default FleetPanel;
